import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import Table, {
  type TableRowProps,
} from 'src/components/molecules/tables/table/table';
import ViewContainer from 'src/components/atoms/view-container/view-container';
import React from 'react';
import { type Contact, type User } from 'src/api/generated';
import PhoneNumberItem from 'src/components/molecules/phone/phone-number-item/phone-number-item';
import Finder from 'src/components/molecules/finder/finder';
import ViewFilter from 'src/components/atoms/view-filter/view-filter';
import AgentSelect from 'src/components/molecules/agent-select/agent-select';
import { useAgents } from 'hooks/api/users';
import { useContactsQueryParams } from 'hooks/contacts';
import { useCurrentUser } from 'hooks/users';
import Paginator from 'src/components/molecules/tables/paginator/paginator';
import SortingHeader from 'src/components/molecules/tables/sorting-header/sorting-header';
import { formatFullName } from 'src/utils/formatting/name';
import { findUser } from 'src/utils/users';
import { useContacts } from 'hooks/api/contacts/contacts';

const DEFAULT_SORT_FIELD = 'createdAt';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_PAGE_SIZE = 10;

const ContactsView: React.FC = () => {
  const { users, loading: usersLoading, error: usersError } = useAgents();
  const { currentUser } = useCurrentUser();

  const {
    queryParams,
    selectedAgentId,
    sendAgentSelection,
    sendSearch,
    sortField,
    selectSortField,
    sortOrder,
    setSortOrder,
    page,
    setPage,
  } = useContactsQueryParams(
    users,
    currentUser,
    DEFAULT_SORT_FIELD,
    DEFAULT_SORT_ORDER,
    DEFAULT_PAGE_SIZE
  );
  const {
    contacts,
    totalPages,
    loading: contactViewsLoading,
    error: contactViewsError,
  } = useContacts(queryParams);

  const rows: TableRowProps[] = contacts.map((contact) =>
    buildRow(contact, users)
  );

  const loading = contactViewsLoading || usersLoading;
  const error = contactViewsError ?? usersError;

  // TODO: check when implementing i18n
  const columns = [
    {
      header: 'ID',
      accessor: 'id',
    },
    {
      header: 'Teléfono',
      accessor: 'phone',
    },
    {
      header: 'Agente asignado',
      accessor: 'userId',
    },
    {
      header: 'Nombre',
      accessor: 'firstName',
    },
    {
      header: 'Email',
      accessor: 'email',
    },
    {
      header: 'Cargo',
      accessor: 'companyPosition',
    },
    {
      header: 'DNI / RUT',
      accessor: 'nationalId',
    },
  ];

  const headerElement = (
    <ViewFilter>
      <AgentSelect
        agents={users}
        selectedAgent={selectedAgentId}
        onAgentSelectionChange={sendAgentSelection}
      />
      <Finder onSearch={sendSearch} />
    </ViewFilter>
  );

  const sortableColumns = columns.map((column) => ({
    ...column,
    header: (
      <SortingHeader
        header={column.header}
        accessor={column.accessor}
        sortField={sortField}
        onSetSortField={selectSortField}
        order={sortOrder}
        onSetOrder={setSortOrder}
      />
    ),
  }));

  return (
    <ViewContainer
      // TODO: check when implementing i18n
      title="Prospectos"
      headerElement={headerElement}
      loading={loading}
      error={error}
    >
      <Table columns={sortableColumns} data={rows} />
      <Paginator
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </ViewContainer>
  );
};

const buildRow = (contact: Contact, users: User[]): TableRowProps => {
  const contactName = formatFullName(contact.firstName, contact.lastName);
  const agent = findUser(contact.userId ?? null, users);
  const agentName = formatFullName(agent?.firstName, agent?.lastName);
  const contactId = contact.id;
  const contactPhone = contact.phone ?? '';

  const phoneNumberElement = () => {
    return <PhoneNumberItem phoneNumber={contactPhone} />;
  };

  const tableRow: TableRowProps = {
    key: contactId.toString(),
    row: [
      { accesor: 'id', value: contactId },
      {
        accesor: 'phone',
        value: contactPhone,
        element: phoneNumberElement(),
      },
      {
        accesor: 'userId',
        value: agentName || '-',
      },
      { accesor: 'firstName', value: contactName || '-' },
      { accesor: 'email', value: nonEmptyString(contact.email) ?? '-' },
      {
        accesor: 'companyPosition',
        value: nonEmptyString(contact.companyPosition) ?? '-',
      },
      {
        accesor: 'nationalId',
        value: nonEmptyString(contact.nationalId) ?? '-',
      },
    ],
  };
  return tableRow;
};

export default ContactsView;
